import type { CatalogComponents } from "@steelyard/api-client";
import { catalog, unwrap } from "../api";
import type { PagedResult } from "../../components/table/data-table";
import { importBatchPath, isGuid, redirectToCanonical } from "../paths";

/**
 * Catalog service calls, through the generated catalog client (FR-006). Same gateway paths as
 * apps/admin/lib/api.ts's `catalog()`/`catalogWritable()` clients previously hit.
 */
export type Brand = {
  id: string;
  name: string;
  code: string;
  /** The public page address segment. Assigned at create; changes only when an admin edits it. */
  slug: string;
  /** Content-addressed hash from `POST /api/files/images`, rendered via `imageUrl` (`@steelyard/common`). */
  logoHash: string | null;
  isActive: boolean;
};

/** Catalog's own page-size ceiling (`Paging.MaxPageSize`) — a larger request falls back to 50, so this is the most one read returns. */
export const CATALOG_MAX_PAGE_SIZE = 200;

/**
 * A form dropdown's options: active records only (project owner, 2026-09-29), plus any inactive one
 * the record being edited already points at — dropping that would blank the field and make an
 * untouched save fail. Create forms ask the API for `activeOnly` instead and need no `keep`.
 */
export function activeOrKept<T extends { id: string; isActive: boolean }>(
  result: PagedResult<T>,
  keep: readonly (string | null | undefined)[],
): PagedResult<T> {
  const items = result.items.filter((row) => row.isActive || keep.includes(row.id));
  return { ...result, items, totalCount: items.length };
}

type ActiveRead<T> =(query: { page: number; pageSize: number; activeOnly: boolean }) => Promise<PagedResult<T>>;

/**
 * One view (All / Active / Inactive) of an active-flagged catalogue list, and every count its status
 * dropdown shows — whichever view is on screen. Catalog lists filter on `activeOnly` and nothing
 * else, so the Inactive rows (and their count) come from one whole read of the list, kept to the
 * rows the API marked `isActive: false`; All and Active are the API's own `totalCount`s. No count is
 * ever "all − active". ponytail: the whole read is one page at the API's 200-row ceiling — past it
 * the Inactive count is absent and the Inactive view says it is partial; an `inactiveOnly` param on
 * the backend lifts both.
 */
export async function loadActiveView<T extends { isActive: boolean }>(
  view: "all" | "active" | "inactive",
  read: ActiveRead<T>,
  paging: { page: number; pageSize: number },
) {
  const [every, active, shown] = await Promise.all([
    // Required for the Inactive view; elsewhere a failure costs only the counts.
    view === "inactive" ? read({ page: 1, pageSize: CATALOG_MAX_PAGE_SIZE, activeOnly: false }) : read({ page: 1, pageSize: CATALOG_MAX_PAGE_SIZE, activeOnly: false }).catch(() => null),
    read({ page: 1, pageSize: 1, activeOnly: true }).catch(() => null),
    view === "inactive" ? null : read({ ...paging, activeOnly: view === "active" }),
  ]);
  const inactive = every ? every.items.filter((row) => !row.isActive) : [];
  const complete = every ? every.items.length >= Number(every.totalCount) : false;
  if (view !== "inactive" && !shown) throw new Error("The API returned no body for this list.");
  return {
    result: view === "inactive" ? { items: inactive, page: 1, pageSize: inactive.length, totalCount: inactive.length } : shown!,
    /** False only when the Inactive view is showing a partial list (more rows than one read holds). */
    complete: view !== "inactive" || complete,
    counts: {
      // The shown page's own total stands in when the separate read for that count failed.
      all: every ? Number(every.totalCount) : view === "all" && shown ? Number(shown.totalCount) : null,
      active: active ? Number(active.totalCount) : view === "active" && shown ? Number(shown.totalCount) : null,
      inactive: every && complete ? inactive.length : null,
    },
  };
}

export async function listBrands(page: number, activeOnly: boolean, pageSize = 50) {
  return unwrap(
    (await catalog()).GET("/api/catalog/brands", { params: { query: { page, pageSize, activeOnly } } }),
  ) as Promise<PagedResult<Brand>>;
}

export async function getBrand(id: string) {
  return unwrap((await catalog()).GET("/api/catalog/brands/{id}", { params: { path: { id } } })) as Promise<Brand>;
}

export async function getBrandBySlug(slug: string) {
  return unwrap((await catalog()).GET("/api/catalog/brands/by-slug/{slug}", { params: { path: { slug } } })) as Promise<Brand>;
}

export async function createBrand(input: { name: string; code: string; logoHash: string | null }) {
  return unwrap((await catalog()).POST("/api/catalog/brands", { body: input }));
}

/** `code` is deliberately absent — CreateBrandRequest's code is immutable once saved. `slug`: absent/null = unchanged. */
export async function updateBrand(id: string, input: { name: string; logoHash: string | null; slug?: string | null }) {
  return unwrap((await catalog()).PUT("/api/catalog/brands/{id}", { params: { path: { id } }, body: input }));
}

/** Deactivates, never removes — sets isActive=false server-side. */
export async function deactivateBrand(id: string) {
  return unwrap((await catalog()).DELETE("/api/catalog/brands/{id}", { params: { path: { id } } }));
}

/** Reverses `deactivateBrand`: the record is offered again. Nothing was deleted, so nothing is re-created. */
export async function reactivateBrand(id: string) {
  return unwrap((await catalog()).POST("/api/catalog/brands/{id}/reactivate", { params: { path: { id } } }));
}

/** CategoryView — `depth`/`isSubcategory` are server-computed from the tree shape, never derived here. */
export type Category = {
  id: string;
  parentId: string | null;
  name: string;
  code: string;
  slug: string;
  sortOrder: number;
  depth: number;
  isSubcategory: boolean;
  isActive: boolean;
  /** Content-addressed hash from `POST /api/files/images`, rendered via `imageUrl` (`@steelyard/common`). */
  imageHash: string | null;
};

/**
 * `GET /categories/tree` returns the WHOLE tree in one unpaginated `PagedResult`
 * (`CatalogueQueryHandler.GetCategoryTreeAsync` calls `PagedResult.Complete`), parents-before-
 * children — never re-sorted client-side (FR-008). The flat, alphabetical `GET /categories` is a
 * different endpoint for a different purpose and is not called from this screen.
 */
export async function listCategoryTree(activeOnly?: boolean) {
  return unwrap(
    (await catalog()).GET("/api/catalog/categories/tree", { params: { query: { activeOnly } } }),
  ) as Promise<PagedResult<Category>>;
}

export async function getCategory(id: string) {
  return unwrap(
    (await catalog()).GET("/api/catalog/categories/{id}", { params: { path: { id } } }),
  ) as Promise<Category>;
}

export async function getCategoryBySlug(slug: string) {
  return unwrap(
    (await catalog()).GET("/api/catalog/categories/by-slug/{slug}", { params: { path: { slug } } }),
  ) as Promise<Category>;
}

export async function createCategory(input: {
  name: string;
  code: string;
  parentId: string | null;
  sortOrder?: number;
  imageHash?: string | null;
}) {
  return unwrap((await catalog()).POST("/api/catalog/categories", { body: { ...input, sortOrder: input.sortOrder ?? 0 } }));
}

/** `code` is deliberately absent — same immutable-once-saved rule as `updateBrand`. */
export async function updateCategory(id: string, input: { name: string; sortOrder: number; imageHash: string | null; slug?: string | null }) {
  return unwrap((await catalog()).PUT("/api/catalog/categories/{id}", { params: { path: { id } }, body: input }));
}

/** Its own endpoint, not part of `updateCategory` — `UpdateCategoryRequest` has no `parentId`. */
export async function reparentCategory(id: string, parentId: string | null) {
  return unwrap(
    (await catalog()).PUT("/api/catalog/categories/{id}/parent", { params: { path: { id } }, body: { parentId } }),
  );
}

/** Deactivates, never removes — same as `deactivateBrand`/`deactivateProduct` (T047). */
export async function deactivateCategory(id: string) {
  return unwrap((await catalog()).DELETE("/api/catalog/categories/{id}", { params: { path: { id } } }));
}

/** Reverses `deactivateCategory`: the record is offered again. Nothing was deleted, so nothing is re-created. */
export async function reactivateCategory(id: string) {
  return unwrap((await catalog()).POST("/api/catalog/categories/{id}/reactivate", { params: { path: { id } } }));
}

/**
 * ProductView — a marketable item under one brand and one category (T046, FR-009/§17).
 * `primaryImageHash` is the packing-device image `updateProduct` sets — a different field
 * from `images` below (the storefront gallery). `images` holds content-addressed hashes from
 * `POST /api/files/images` (the same shared upload endpoint brand/category logos use), rendered
 * via `imageUrl` — not arbitrary caller-typed URLs.
 */
export type Product = {
  id: string;
  brandId: string;
  categoryId: string;
  name: string;
  /** The admin URL segment. Assigned at create; changes only when an admin edits it. */
  slug: string;
  description: string | null;
  images: string[];
  isActive: boolean;
  primaryImageHash: string | null;
  /** Catalog's own names for `brandId`/`categoryId`; null when unresolved. */
  brandName?: string | null;
  categoryName?: string | null;
};

export async function listProducts(params: { page: number; activeOnly: boolean; brandId?: string; categoryId?: string; q?: string; pageSize?: number }) {
  return unwrap(
    (await catalog()).GET("/api/catalog/products", {
      params: {
        query: {
          page: params.page,
          pageSize: params.pageSize ?? 50,
          activeOnly: params.activeOnly,
          brandId: params.brandId,
          categoryId: params.categoryId,
          // Server search over every product: its name or slug, or any of its SKUs' name or code.
          // TODO(016 T080): remove cast after openapi regen (the snapshot predates `q`).
          ...({ q: params.q } as unknown as Record<string, never>),
        },
      },
    }),
  ) as Promise<PagedResult<Product>>;
}

export async function getProduct(id: string) {
  return unwrap((await catalog()).GET("/api/catalog/products/{id}", { params: { path: { id } } })) as Promise<Product>;
}

export async function getProductBySlug(slug: string) {
  return unwrap((await catalog()).GET("/api/catalog/products/by-slug/{slug}", { params: { path: { slug } } })) as Promise<Product>;
}

export type ProductInput = { brandId: string; categoryId: string; name: string; description: string | null; images: string[] | null };

/**
 * `CreateProduct`'s declared response carries no body (GAP-4 residual — creates return an
 * anonymous `new { id }` the document doesn't declare), but `CatalogueController.CreateProduct`
 * really answers `Created($"/api/catalog/products/{id}", new { id })`. Narrow runtime-parse cast,
 * the same idiom as `createSku`/`setSkuUnits` above — so the caller can navigate straight to the
 * new product instead of only the list.
 */
export async function createProduct(input: ProductInput) {
  const data = await unwrap((await catalog()).POST("/api/catalog/products", { body: input }));
  return data as unknown as { id: string };
}

/**
 * `slug`: absent/null = unchanged — same as `updateBrand`. `primaryImageHash`: the packing-device
 * image, uploaded to Files first (016); absent = unchanged.
 */
export async function updateProduct(id: string, input: ProductInput & { slug?: string | null; primaryImageHash?: string }) {
  return unwrap((await catalog()).PUT("/api/catalog/products/{id}", { params: { path: { id } }, body: input }));
}

/** Deactivates, never removes — same as `deactivateBrand`. */
export async function deactivateProduct(id: string) {
  return unwrap((await catalog()).DELETE("/api/catalog/products/{id}", { params: { path: { id } } }));
}

/** Reverses `deactivateProduct`: the record is offered again. Nothing was deleted, so nothing is re-created. */
export async function reactivateProduct(id: string) {
  return unwrap((await catalog()).POST("/api/catalog/products/{id}/reactivate", { params: { path: { id } } }));
}

/**
 * `PriceRoundingMode`, `DiscountScope`, `PriceSource`, `StaleReason` and `UnitView.type` below are
 * all hand-typed string-literal unions rather than imports of `@steelyard/api-client`'s generated
 * schema, on purpose: the catalog OpenAPI document describes every one of them as a bare
 * `integer`, but the service still serializes them with a snake_case `JsonStringEnumConverter` —
 * verified live against the running gateway 2026-09-14 (`GET /api/catalog/tiers` answers
 * `"priceRoundingMode":"none"`, never `0`; same for `GET /api/catalog/units`' `"type":"weight"`).
 * The generated types are wrong until that's fixed upstream, so every call site below that reads
 * or writes one of these fields casts through `unknown` rather than trusting the generated shape —
 * the same narrow-cast idiom this file already uses for GAP-4's undeclared response bodies.
 */
export type PriceRoundingMode = "none" | "rupee" | "nearest_5";

export type CreateTierRequest = {
  name: string;
  seedDiscountPct: number;
  allowPiecePurchase: boolean;
  allowUnitPurchase: boolean;
  priceRoundingMode: PriceRoundingMode;
  effectiveFrom: string;
};

export async function createTier(body: CreateTierRequest) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- see the enum-gap comment above `PriceRoundingMode`
  return unwrap((await catalog()).POST("/api/catalog/tiers", { body: body as any }));
}

/** TierView, widened from the old `{id, name, isDefault}` shape for T059's tier list (§21, FR-014). */
export type Tier = {
  id: string;
  name: string;
  /** The admin URL segment — assigned at create, never changed (not even by a rename). */
  slug: string;
  isDefault: boolean;
  seedDiscountPct: number;
  allowPiecePurchase: boolean;
  allowUnitPurchase: boolean;
  priceRoundingMode: PriceRoundingMode;
  effectiveFrom: string;
  isActive: boolean;
  /** The tier a company starts on when it registers, B2B from the outset (at most one tier). */
  forNewCompanies: boolean;
};

/**
 * Used by 007-inventory's stock list to find the Default Tier's id (BR-01) — see `listCatalogue`
 * — and by T059's tier list. `listTiers()` with no args keeps that caller's exact prior behaviour
 * (page 1, pageSize 100, every tier regardless of `isActive`).
 */
export async function listTiers(params?: { page?: number; pageSize?: number; activeOnly?: boolean }) {
  return unwrap(
    (await catalog()).GET("/api/catalog/tiers", {
      params: { query: { page: params?.page ?? 1, pageSize: params?.pageSize ?? 100, activeOnly: params?.activeOnly } },
    }),
  ) as unknown as Promise<PagedResult<Tier>>;
}

export type CatalogueItem = {
  skuId: string;
  skuCode: string;
  /** The SKU's slug — the key of its `/inventory/{slug}` and `/catalogue/skus/{slug}` URLs. */
  slug: string;
  name: string;
  brandId: string;
  brandName: string;
  categoryId: string;
  primaryUnitId: string;
  primaryUnitCode: string;
  rate: number | null;
  gstTreatment: string | null;
  priced: boolean;
};

/**
 * 007-inventory's stock list has no `GET /stock` (paged, all SKUs) to page from — Warehouse's
 * `FreeStockView` is keyed by `skuId` alone and answers one SKU at a time (verified against
 * `InventoryController.cs`/`contracts/screen-routes.md` row 1: "`GET /stock/{skuId}` per row,
 * paged"). This is the only paged, SKU-shaped listing route in the system, so it is what the
 * inventory list pages SKU identity from; the stock figures themselves still come only from
 * `getStock` (warehouse.ts), never from here.
 */
export async function listCatalogue(params: { tierId: string; page: number; pageSize?: number; q?: string }) {
  return unwrap(
    (await catalog()).GET("/api/catalog/catalogue", {
      params: {
        query: { tierId: params.tierId, page: params.page, pageSize: params.pageSize ?? 25, q: params.q },
      },
    }),
  ) as Promise<PagedResult<CatalogueItem>>;
}

/** SkuAttributes — the generated `isEmpty` field is server-computed and never submitted. */
export type SkuAttributes = {
  size?: string | null;
  thickness?: string | null;
  grade?: string | null;
  finish?: string | null;
  length?: string | null;
};

export type SkuUnitRow = {
  unitId: string;
  unitCode: string;
  conversionToStocking: number;
  isPrimary: boolean;
  isSellable: boolean;
  decimals: number;
};

/**
 * SkuView (T048/T049/T051 — catalogue-sku-editor). Widened from the old `{ id, code, name }` shape
 * so this one `getSku` also serves the SKU editor and units screens; `inventory/Detail.tsx` and
 * `inventory/Exceptions.tsx` only ever read `id`/`code`/`name` off it, so this is a safe superset.
 */
export type SkuView = {
  id: string;
  productId: string;
  code: string;
  slug: string;
  brandSlug: string;
  categorySlug?: string | null;
  name: string;
  hsnCode: string;
  gstRateId: string;
  attributes: SkuAttributes;
  stockingUnitId: string;
  primaryUnitId: string | null;
  batchTracked: boolean;
  isActive: boolean;
  units: SkuUnitRow[];
  /** Catalog's labels beside the ids; null when unresolved. */
  productName?: string | null;
  stockingUnitCode?: string | null;
  primaryUnitCode?: string | null;
};

/** Alias kept so `inventory/{Detail,Exceptions}.tsx` need no changes beyond their test fixtures. */
export type SkuDetail = SkuView;

export async function getSku(id: string) {
  return unwrap(
    (await catalog()).GET("/api/catalog/skus/{id}", { params: { path: { id } } }),
  ) as Promise<SkuView>;
}

export async function getSkuBySlug(slug: string) {
  return unwrap(
    (await catalog()).GET("/api/catalog/skus/by-slug/{slug}", { params: { path: { slug } } }),
  ) as Promise<SkuView>;
}

export type CreateSkuRequest = {
  productId: string;
  code: string;
  name: string;
  hsnCode: string;
  gstRateId: string;
  stockingUnitId: string;
  attributes: SkuAttributes | null;
};

/**
 * `CreateSku`'s generated response is `content?: never` — undeclared, GAP-4's residual tail — but
 * `CatalogueController.CreateSku` actually returns `Created($"/api/catalog/skus/{id}", new { id })`,
 * a real 201 body. Verified live against the running gateway 2026-09-10. Narrow runtime-parse cast,
 * the same idiom `lib/api.ts`'s own doc comment sanctions (`parseAccount`, `parsePermissions`, ...) —
 * not a hand-typed DTO standing in for a whole invented shape.
 */
export async function createSku(body: CreateSkuRequest) {
  const data = await unwrap((await catalog()).POST("/api/catalog/skus", { body }));
  return data as unknown as { id: string };
}

/** `slug`: absent/null = unchanged. */
export type UpdateSkuRequest = { name: string; hsnCode: string; gstRateId: string; attributes: SkuAttributes | null; slug?: string | null };

/** `productId`/`code`/`stockingUnitId` are fixed at creation (FR-009) — absent here on purpose. */
export async function updateSku(id: string, body: UpdateSkuRequest) {
  return unwrap((await catalog()).PUT("/api/catalog/skus/{id}", { params: { path: { id } }, body }));
}

/** Deactivates, never removes — same as `deactivateBrand`. Left for T047 to wire up. */
export async function deactivateSku(id: string) {
  return unwrap((await catalog()).DELETE("/api/catalog/skus/{id}", { params: { path: { id } } }));
}

/** Reverses `deactivateSku`: the record is offered again. Nothing was deleted, so nothing is re-created. */
export async function reactivateSku(id: string) {
  return unwrap((await catalog()).POST("/api/catalog/skus/{id}/reactivate", { params: { path: { id } } }));
}

/**
 * SkuListItemView (T103, GAP-1 closed — `GET /skus` now exists, verified live against the running
 * gateway 2026-09-15). Denormalized `brandName`/`categoryName` mean the list needs no join against
 * `listBrands`/`listCategoryTree` the way `Product`'s list does above. No enum field here — every
 * member is a plain string/number/boolean — so none of this type needs the `unknown`-cast idiom
 * `PriceRoundingMode` etc. require above.
 */
export type SkuListItem = {
  id: string;
  code: string;
  slug: string;
  name: string;
  productId: string;
  brandName: string;
  categoryName: string;
  stockingUnitId: string;
  primaryUnitId: string | null;
  sellableUnitCount: number;
  isIncomplete: boolean;
  isActive: boolean;
};

/** `q` searches code and name server-side (verified against `ListSkusAsync`) — never filtered client-side. */
export async function listSkus(params: {
  page: number;
  pageSize?: number;
  activeOnly?: boolean;
  q?: string;
  productId?: string;
  brandId?: string;
  categoryId?: string;
  incompleteOnly?: boolean;
}) {
  return unwrap(
    (await catalog()).GET("/api/catalog/skus", {
      params: {
        query: {
          page: params.page,
          pageSize: params.pageSize ?? 50,
          activeOnly: params.activeOnly,
          q: params.q,
          productId: params.productId,
          brandId: params.brandId,
          categoryId: params.categoryId,
          incompleteOnly: params.incompleteOnly,
        },
      },
    }),
  ) as Promise<PagedResult<SkuListItem>>;
}

export type UnitOption = { id: string; code: string; name: string; type: string; decimals: number };

/** `GET /units` takes no query parameters at all — it always returns the whole reference table. */
export async function listUnits() {
  return unwrap((await catalog()).GET("/api/catalog/units")) as unknown as Promise<PagedResult<UnitOption>>;
}

/** GstRateView (BUG-08) — Catalog's own registry, replacing the free-text GST-rate-id field. */
export type GstRate = { id: string; label: string; percent: number; isActive: boolean };

export async function listGstRates(activeOnly = false) {
  return unwrap(
    (await catalog()).GET("/api/catalog/gst-rates", { params: { query: { activeOnly } } }),
  ) as Promise<PagedResult<GstRate>>;
}

export async function createGstRate(input: { label: string; percent: number }) {
  const data = await unwrap((await catalog()).POST("/api/catalog/gst-rates", { body: input }));
  return data as unknown as { id: string };
}

export async function updateGstRate(id: string, input: { label: string; percent: number; isActive: boolean }) {
  await unwrap((await catalog()).PUT("/api/catalog/gst-rates/{id}", { params: { path: { id } }, body: input }));
}

/** 409 "GST rate in use" while any SKU is taxed at it — deactivate instead. */
export async function deleteGstRate(id: string) {
  await unwrap((await catalog()).DELETE("/api/catalog/gst-rates/{id}", { params: { path: { id } } }));
}

/**
 * BasePriceView/BasePricesView (T058 — base-price editor). Hand-typed, same treatment as
 * `Brand`/`SkuDetail` above: the generated schema exists (`catalog.d.ts` `BasePricesView`/
 * `BasePriceView`) but this file's own convention is a plain local type over the generated one, not
 * an import of it.
 */
export type BasePriceView = {
  unitId: string;
  unitCode: string;
  rate: number | null;
  gstTreatment: "inclusive" | "exclusive" | null;
  priced: boolean;
  isPrimary: boolean;
  conversionToStocking: number;
};

export type BasePricesView = { skuId: string; skuCode: string; defaultTierId: string; prices: BasePriceView[] };

export async function getBasePrices(skuId: string) {
  return unwrap(
    (await catalog()).GET("/api/catalog/skus/{skuId}/base-prices", { params: { path: { skuId } } }),
  ) as Promise<BasePricesView>;
}

/**
 * `SetBasePrices`'s generated 200 is `content?: never` — undeclared, same GAP-4 tail as `createSku`/
 * `setSkuUnits` above, but here nothing is cast around it: `BasePricesController.SetBasePrices`
 * really does return a body (`SetBasePricesResponse`), just without the `[ProducesResponseType]`
 * attribute that would put it in the OpenAPI document. The caller re-fetches `getBasePrices` instead
 * of reading this call's return, so no cast is needed either way.
 */
/**
 * D-04: GST treatment is configurable per price; omitted, the API applies the unit's configured
 * default. Documented as `integer`, serialized as a string — the enum gap above `PriceRoundingMode`.
 */
export type GstTreatment = "inclusive" | "exclusive";

export async function setBasePrices(skuId: string, prices: { unitId: string; rate: number; gstTreatment?: GstTreatment }[]) {
  return unwrap(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- see the enum-gap comment above `PriceRoundingMode`
    (await catalog()).PUT("/api/catalog/skus/{skuId}/base-prices", { params: { path: { skuId } }, body: { prices } as any }),
  );
}

export type SkuUnits = { skuId: string; skuCode: string; stockingUnitId: string; units: SkuUnitRow[] };

export async function getSkuUnits(skuId: string) {
  return unwrap(
    (await catalog()).GET("/api/catalog/skus/{id}/units", { params: { path: { id: skuId } } }),
  ) as Promise<SkuUnits>;
}

export type SellableUnitInput = { unitId: string; conversionToStocking: number; isPrimary: boolean; isSellable: boolean };

/**
 * Replaces the whole set — "exactly one primary" and "Number is always present" are properties of
 * the set, not a single row (D-01, FR-010, FR-011). `SetSkuUnits`'s generated response is also
 * `content?: never`, but `SetSkuUnitsHandler.HandleAsync` returns `SetSkuUnitsResult(int
 * SellableUnitCount)` and the controller does `Ok(result)` — same undeclared-body/narrow-cast
 * situation as `createSku`, verified live against the same running gateway 2026-09-10.
 */
export async function setSkuUnits(skuId: string, units: SellableUnitInput[]) {
  const data = await unwrap(
    (await catalog()).PUT("/api/catalog/skus/{id}/units", { params: { path: { id: skuId } }, body: { units } }),
  );
  return data as unknown as { sellableUnitCount: number };
}

/**
 * 008-reporting-dashboards T009/T021 — the dashboard's stale-tier-prices tile, and
 * `/reports/staleness` (FR-014, D-05). `criticalCount` and `varianceThresholdPct` are the tile's own
 * count and label; `rows` is the full paged breakdown the report screen pages through. Verified live
 * against the running gateway 2026-09-09: `{criticalCount:0, varianceThresholdPct:10.00, empty:true,
 * emptyReason:"no_stale_tier_prices", rows:{items:[],totalCount:0,...}}` with no tiers priced yet.
 *
 * `dateField` is always `"none"` on this report — the backend's own contract
 * (`008-reporting/contracts/reports.md` §"Where the shipped routes differ", item 3) says this and
 * the weight-collision report are statements about the catalogue as it stands, not about a period,
 * so the report screen has no from/to form to build.
 */
export type StaleReason = "base_price_moved" | "newly_added" | "rule_changed" | null;

/** One tier price whose derived rate has drifted from the base price it was struck from (FR-013). */
export type TierStalenessRow = {
  tierPriceId: string;
  tierId: string;
  tierName: string;
  skuId: string;
  skuCode: string;
  unitId: string;
  unitCode: string;
  rate: number;
  derivedFromBaseRate: number | null;
  currentBaseRate: number | null;
  /** Movement as a value and a percentage — both, never just one (FR-013). */
  movementValue: number | null;
  movementPct: number | null;
  reason: StaleReason;
  /** Listed but excluded from re-apply, not hidden (FR-014, D-05). */
  manuallySet: boolean;
  excludedFromReapplyByDefault: boolean;
  /** Past `varianceThresholdPct`, compared unrounded server-side (FR-015) — never recomputed here. */
  critical: boolean;
};

export type TierStalenessReport = {
  dateField: string;
  sources: string[];
  empty: boolean;
  emptyReason: string | null;
  producedAt: string;
  varianceThresholdPct: number;
  criticalCount: number;
  /** FR-016/D-06's own sentence — rendered verbatim: a stale price never blocks ordering. */
  orderingImpact: string;
  rows: PagedResult<TierStalenessRow>;
};

/** Presentation only — the wire value itself (`base_price_moved`, etc.) is what a caller compares against. */
export function staleReasonLabel(reason: StaleReason): string {
  switch (reason) {
    case "base_price_moved":
      return "Base price moved";
    case "newly_added":
      return "Newly added";
    case "rule_changed":
      return "Discount rule changed";
    default:
      return "—";
  }
}

export async function getTierStalenessReport(params?: { tierId?: string; reason?: string; page?: number; pageSize?: number }) {
  return unwrap(
    (await catalog()).GET("/api/catalog/reports/tier-staleness", { params: { query: params ?? {} } }),
  ) as unknown as Promise<TierStalenessReport>;
}

/**
 * `GET /api/catalog/reports/tier-staleness?format=csv` is the same route as
 * `getTierStalenessReport`, with the paged JSON envelope swapped for a plain CSV file of the full
 * filtered result — not one page of it — following the `?format=csv` convention the backend's other
 * report routes already use. A file download, not a JSON call, so this returns the URL for an `<a>`
 * (same shape as `importTemplateUrl` above) rather than fetching it through `unwrap`. Only
 * `tierId`/`reason` are passed: `page`/`pageSize` describe the on-screen page, and CSV mode returns
 * everything the filter matches — the reason `Staleness.tsx` links straight here instead of
 * paginating through the whole server-paged result client-side (`export-guard.test.ts`'s own header
 * comment is the record of why that report can't use the shared `ExportButton`).
 */
export function tierStalenessReportCsvUrl(params?: { tierId?: string; reason?: string }): string {
  const query = new URLSearchParams({ format: "csv" });
  if (params?.tierId) query.set("tierId", params.tierId);
  if (params?.reason) query.set("reason", params.reason);
  return `/api/catalog/reports/tier-staleness?${query.toString()}`;
}

/**
 * `GET /api/catalog/staleness` — catalogue-wide stale counts, `byReason` keyed by the same wire
 * reason values the report's `reason` filter takes. Not tier-scoped: a tier-filtered view must not
 * show these as its own counts.
 */
export async function getStalenessSummary() {
  return unwrap((await catalog()).GET("/api/catalog/staleness")) as unknown as Promise<{
    staleCount: number;
    criticalCount: number;
    byReason: Record<string, number>;
    varianceThresholdPct: number;
  }>;
}

/**
 * 001-catalogue-admin-screens T062/T063/T064 (discount-rules-wizard). `DiscountScope` is one of
 * the two enums that reach the generated client because it appears in a *request* schema
 * (data-model.md §3) — mirrored here as a local union, same convention as `PriceRoundingMode`
 * above, not imported from `@steelyard/api-client`.
 */
export type DiscountScope = "all" | "brand" | "category" | "subcategory" | "sku";

/**
 * DiscountRuleView (data-model §3) — a tier's rule list, always returned in `sequence` order
 * (pricing-admin.md: "sequence decides the outcome", FR-022). `scopeResolved: false` with a
 * `scopeIssue` is a rule pointing at a deleted or deactivated brand/category — rendered as
 * broken, never filtered out (data-model.md, the spec's own edge case).
 */
export type DiscountRuleView = {
  id: string;
  sequence: number;
  scope: DiscountScope;
  scopeId: string | null;
  scopeName: string | null;
  discountPct: number;
  scopeResolved: boolean;
  scopeIssue: string | null;
};

/** `GET /tiers/{id}/rules` is a complete set in one page — never paged further (pricing-admin.md). */
export async function getTierRules(tierId: string) {
  return unwrap(
    (await catalog()).GET("/api/catalog/tiers/{id}/rules", { params: { path: { id: tierId } } }),
  ) as unknown as Promise<PagedResult<DiscountRuleView>>;
}

/** DiscountRuleRequest (data-model §3) — what the builder submits, ordered by array position. */
export type DiscountRuleInput = {
  sequence: number;
  scope: DiscountScope;
  scopeId: string | null;
  discountPct: number;
};

export type SetTierRulesResult = { rulesSaved: number; rowsFlaggedStale: number };

/**
 * `SetTierRules`'s generated 200 is `content?: never` — same GAP-4 tail as `createSku`/
 * `setSkuUnits` above, but `TiersController.SetRules` really returns
 * `Ok(new SetTierRulesResponse(RulesSaved, RowsFlaggedStale))` (pricing-admin.md,
 * `../Steelyard/.../TiersController.cs`), just without the `[ProducesResponseType]` that would put
 * it in the OpenAPI document. Narrow runtime-parse cast, the same idiom as `createSku`/`setSkuUnits`.
 * Saving flags every affected `from_discount` row `rule_changed` and writes no rate (FR-030,
 * BR-04) — this is a proposal a re-apply commits, not a repricing.
 */
export async function setTierRules(tierId: string, rules: DiscountRuleInput[]) {
  const data = await unwrap(
    (await catalog()).PUT("/api/catalog/tiers/{id}/rules", {
      params: { path: { id: tierId } },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- see the enum-gap comment above `PriceRoundingMode`
      body: { rules } as any,
    }),
  );
  return data as unknown as SetTierRulesResult;
}

/**
 * T061 (tier settings edit) — TierSummaryDto. `TiersController.cs`'s own comment records that
 * `GET /tiers/{id}` is served by `PricingController.GetTierSummary`, not `TiersController` itself:
 * "both contracts document the route, and the consumer shape is the wider one — it resolves
 * `isEffective` (FR-019a) rather than leaving every caller to compare dates itself." The wire
 * field is `tierId`; remapped to `id` to match every other entity's shape in this file.
 */
export type TierSettings = {
  id: string;
  name: string;
  slug: string;
  isDefault: boolean;
  allowPiecePurchase: boolean;
  allowUnitPurchase: boolean;
  priceRoundingMode: PriceRoundingMode;
  effectiveFrom: string;
  isEffective: boolean;
  isActive: boolean;
  forNewCompanies: boolean;
};

export async function getTier(id: string) {
  const { tierId, ...rest } = await unwrap(
    (await catalog()).GET("/api/catalog/tiers/{tierId}", { params: { path: { tierId: id } } }),
  );
  return { id: tierId, ...rest } as unknown as TierSettings;
}

export async function getTierBySlug(slug: string) {
  const { tierId, ...rest } = await unwrap(
    (await catalog()).GET("/api/catalog/tiers/by-slug/{slug}", { params: { path: { slug } } }),
  );
  return { id: tierId, ...rest } as unknown as TierSettings;
}

/**
 * A record route's key (admin URL contract): a slug is read by slug; a GUID — a bookmark or a link
 * from before slugs — is read by id and answered with a redirect to the slug URL, keeping the rest
 * of the path (`/units`, `/rules`) and the query. An unknown slug is the read's own 404. A loader
 * passes the two reads itself (`byKey(request, params.slug!, getBrand, getBrandBySlug, brandPath)`).
 */
export async function byKey<T extends { slug: string }>(
  request: Request,
  key: string,
  byId: (id: string) => Promise<T>,
  bySlug: (slug: string) => Promise<T>,
  path: (slug: string) => string,
): Promise<T> {
  if (!isGuid(key)) return bySlug(key);
  throw redirectToCanonical(request, key, path((await byId(key)).slug));
}


export type UpdateTierRequest = {
  name: string;
  allowPiecePurchase: boolean;
  allowUnitPurchase: boolean;
  priceRoundingMode: PriceRoundingMode;
  effectiveFrom: string;
  isActive: boolean;
  /** Omitted leaves the mark as it is; `true` moves it here from whichever tier held it. */
  forNewCompanies?: boolean;
};

/**
 * `TiersController.Update` returns `NoContent()` — no `rowsFlaggedStale` or any other body here,
 * unlike `PUT /tiers/{id}/rules` (T064). FR-018a's "cannot deactivate the Default Tier" rule
 * surfaces as a 422 from this call, rendered verbatim by the caller's `ErrorSurface` — never
 * pre-checked client-side.
 */
export async function updateTier(id: string, body: UpdateTierRequest) {
  return unwrap(
    (await catalog()).PUT("/api/catalog/tiers/{id}", {
      params: { path: { id } },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- see the enum-gap comment above `PriceRoundingMode`
      body: body as any,
    }),
  );
}

/**
 * 001-catalogue-admin-screens T077-T080 (US4, FR-022/FR-023, data-model §6 "Re-apply preview").
 * `POST /tiers/{id}/reapply/preview` writes nothing — it stores the rows a re-apply would touch
 * so a later commit reads the same numbers this preview showed (backend
 * `CreateReapplyPreviewHandler`). The controller answers `Created(...)` with a body
 * (`ReapplyPreviewResponse(PreviewId, RowCount, Status)`); the committed OpenAPI snapshot still
 * declares this operation's 2xx with no content (backend-gaps GAP-4, "still outstanding ... US4's
 * re-apply" — line 174), so `unwrap` types the result as `undefined`. Narrow runtime-parse cast,
 * the same idiom as `setTierRules`/`postImport` above.
 */
export type ReapplyPreviewResult = {
  previewId: string;
  /** `RAP-000010` — the preview's URL key (`reapplyPreviewPath`). */
  previewNumber: string;
  /** The tier the preview re-prices, so its URL need not carry it. */
  tierId: string;
  rowCount: number;
  status: string;
};

/** FR-023: excluded unless explicitly opted in — the caller's checkbox, never defaulted true. */
export async function previewReapply(tierId: string, includeManuallySet: boolean) {
  const data = await unwrap(
    (await catalog()).POST("/api/catalog/tiers/{tierId}/reapply/preview", {
      params: { path: { tierId } },
      body: { includeManuallySet },
    }),
  );
  return data as unknown as ReapplyPreviewResult;
}

/** `GET /reapply/by-number/{previewNumber}` — the preview record itself (its rows are `getReapplyRows`). */
export async function getReapplyPreviewByNumber(previewNumber: string) {
  return unwrap(
    (await catalog()).GET("/api/catalog/reapply/by-number/{previewNumber}", { params: { path: { previewNumber } } }),
  ) as Promise<ReapplyPreviewResult>;
}

/** One row's before/after (FR-022) — SC-004a pages through this rather than returning the whole set. */
export type ReapplyRow = {
  tierPriceId: string;
  /** §9.3: joined server-side; null only when the tier price was deleted after the preview. */
  skuCode: string | null;
  skuName: string | null;
  unitCode: string | null;
  currentRate: number;
  proposedRate: number;
  difference: number;
  observedBaseRate: number;
};

export async function getReapplyRows(previewId: string, params?: { page?: number; pageSize?: number }) {
  return unwrap(
    (await catalog()).GET("/api/catalog/reapply/{previewId}/rows", {
      params: { path: { previewId }, query: params ?? {} },
    }),
  ) as Promise<PagedResult<ReapplyRow>>;
}

export type CommitReapplyResult = { rowsUpdated: number };

/**
 * A row whose base rate moved between preview and commit (data-model §5's 409, FR-044a on the
 * backend). Present only on the rejection — empty on success. `currentBaseRate` is `null` when
 * the base price was deleted since the preview, never coerced to a number.
 */
export type MovedBaseRate = {
  skuId: string;
  unitId: string;
  /** §9.3: joined server-side, so the table names the SKU and unit rather than their ids. */
  skuCode?: string | null;
  skuName?: string | null;
  unitCode?: string | null;
  observedBaseRate: number;
  currentBaseRate: number | null;
};

/**
 * FR-022: rejected in full rather than partially, on a 409 carrying `movedRows` (base rates moved)
 * or none (the tier's discount rules moved instead — every row is affected, so there is no row
 * list). The caller never retries this same commit; it takes a fresh preview.
 */
export async function commitReapply(previewId: string) {
  const data = await unwrap(
    (await catalog()).POST("/api/catalog/reapply/{previewId}/commit", { params: { path: { previewId } } }),
  );
  return data as unknown as CommitReapplyResult;
}

/** Housekeeping — abandons an open preview without writing a price (FR-022's other branch). */
export async function abandonReapply(previewId: string) {
  return unwrap((await catalog()).POST("/api/catalog/reapply/{previewId}/abandon", { params: { path: { previewId } } }));
}

// ---------------------------------------------------------------------------------------------
// Bulk import (001 US5 — FR-025/026/027/027a). Two kinds share one wizard and one diff screen:
// screen-routes.md — "/tiers/[tierId]/prices also drives FR-018's tier-price upload through
// POST /imports?kind=tier_price, which lands in the same /imports/[batchId] diff screen. One
// importer, two kinds."
// ---------------------------------------------------------------------------------------------

/** The two kinds `ImportController.TryParseKind` accepts — verified against the backend source. */
export type ImportKind = "catalogue" | "tier_price";

/** The four outcomes an imported row settles into (FR-025/FR-052) — every row is exactly one, never summed into a total. */
export type ImportRowOutcome = "addition" | "change" | "error" | "unchanged";

export const IMPORT_ROW_OUTCOMES: readonly ImportRowOutcome[] = ["addition", "change", "error", "unchanged"];

export function importRowOutcomeLabel(outcome: ImportRowOutcome): string {
  switch (outcome) {
    case "addition":
      return "Additions";
    case "change":
      return "Changes";
    case "error":
      return "Errors";
    case "unchanged":
      return "Unchanged";
  }
}

/**
 * `GET /imports/template?kind=` is a plain CSV file download (`ImportController.GetImportTemplate`
 * returns `File(...)`), not a JSON call — this returns the URL for an `<a>` rather than fetching it
 * through `unwrap`. `apps/admin-bff/src/proxy.js`'s `sendResponse` relays every response's
 * status/content-type but not `Content-Disposition` — outside this bucket's apps/admin +
 * packages/theme scope, so not edited here — which is why the caller sets its own `download`
 * attribute rather than relying on the header.
 */
export function importTemplateUrl(kind: ImportKind): string {
  return `/api/catalog/imports/template?kind=${encodeURIComponent(kind)}`;
}

/**
 * `ValidateImportHandler`'s message when no parser recognises the file (verified against
 * `ValidateImportHandler.cs`: `throw new ImportFileFormatException($"'{fileName}' is not a file
 * this service can read. Upload the published template as CSV (GET /imports/template).")`), which
 * `CatalogueExceptionHandler` answers as a 400 with this exact text as `detail`. Shared so the
 * client-side `.xlsx` rejection (T090) reads identically to the server's own 400 rather than a
 * paraphrase that could drift from it.
 */
export function unreadableImportFileMessage(fileName: string): string {
  return `'${fileName}' is not a file this service can read. Upload the published template as CSV or XLSX (GET /imports/template).`;
}

/** ImportBatchResponse (T199) — the four counts account for every data row in the file (FR-052). */
export type ImportBatchResult = {
  batchId: string;
  /** `IMP-000007`, the batch's URL key — not declared by the upload's response; read when sent. */
  batchNumber?: string | null;
  status: string;
  additionCount: number;
  changeCount: number;
  errorCount: number;
  unchangedCount: number;
};

/**
 * `POST /imports?kind=`. The document declares `200`/`content?: never` (GAP-4's tail), but
 * `ImportController.ValidateImport` actually answers `202 Accepted` with `ImportBatchResponse`
 * (`batchId`, `status`, `additionCount`, `changeCount`, `errorCount`, `unchangedCount`) — a 202 is
 * still `response.ok`, so `unwrap` returns normally. Narrow runtime-parse cast, the same idiom as
 * `createSku`/`setSkuUnits` above. Verified live against the running gateway 2026-09-10.
 */
export async function postImport(kind: ImportKind, fileHash: string, fileName: string) {
  const data = await unwrap(
    (await catalog()).POST("/api/catalog/imports", {
      params: { query: { kind } },
      // The file went to Files first (016); this sends its hash. `fileName` still picks the parser.
      // TODO(016 T080): remove cast after openapi regen
      body: { fileHash, fileName } as unknown as { file: string },
    }),
  );
  return data as unknown as ImportBatchResult;
}

/** ImportRowView (GAP-4 does not touch this operation — it is the one fully-generated response on this surface). */
export type ImportRow = {
  lineNumber: number;
  outcome: ImportRowOutcome;
  /** Null unless `outcome === "error"` (FR-026). */
  errorReason: string | null;
  /** The validated row as JSON — what a commit would apply. Never parsed client-side: its shape differs by import kind and the server already validated it (constitution I). */
  payload: string;
};

export async function getImportRows(batchId: string, params: { outcome?: ImportRowOutcome; page?: number; pageSize?: number } = {}) {
  return unwrap(
    (await catalog()).GET("/api/catalog/imports/{batchId}/rows", {
      params: { path: { batchId }, query: params },
    }),
  ) as Promise<PagedResult<ImportRow>>;
}

export type ImportCommitResult = { rowsApplied: number; rowsSkipped: number };

/** `POST /imports/{batchId}/commit` — same undeclared-body/narrow-cast situation as `postImport`. */
export async function commitImport(batchId: string) {
  const data = await unwrap((await catalog()).POST("/api/catalog/imports/{batchId}/commit", { params: { path: { batchId } } }));
  return data as unknown as ImportCommitResult;
}

/** `POST /imports/{batchId}/abandon` — the real response is 204 No Content; nothing to read. */
export async function abandonImport(batchId: string) {
  return unwrap((await catalog()).POST("/api/catalog/imports/{batchId}/abandon", { params: { path: { batchId } } }));
}

/**
 * `GET /imports` — the batch history a bulk-import screen lists before drilling into any one
 * batch's rows (`ImportController.ListImports` / `ImportQueryHandler.ListBatchesAsync`). Verified
 * directly against `../Steelyard/src/Services/Catalog/Steelyard.Catalog.Api/Controllers/ImportController.cs`
 * and `.../Application/Queries/ImportQueries.cs`'s `ImportBatchListView`, because as of this
 * writing the route is uncommitted backend work in the sibling repo (`git status` there shows
 * `ImportController.cs`/`ImportQueries.cs` modified) — it predates the committed
 * `openapi/catalog.json` snapshot, which still declares only a `post` on `/api/catalog/imports`
 * (`catalog.d.ts`'s `get?: never` for that path). `.GET(...)` on the generated client therefore
 * will not type-check for this operation at all, unlike every other undeclared-*response* call in
 * this file (`createSku`, `setSkuUnits`, ...) — this is the one call on this surface where the
 * whole operation, not just its body, is missing from the snapshot. The cast below is that same
 * narrow-cast idiom, applied to `client.GET` itself rather than to its return value. Replace this
 * with a plain `(await catalog()).GET("/api/catalog/imports", …)` the next time
 * `pnpm snapshot:api catalog && pnpm generate:api` runs against a live backend that has this
 * change committed — `listSkus`'s "GAP-1 closed" comment above is the precedent for that swap.
 */
export type ImportBatchStatus = "validated" | "committed" | "abandoned";

/** ImportBatchListView — one past batch, newest first. The four counts are never summed into a total (same rule as `ImportRow`'s outcomes). */
export type ImportBatchListItem = {
  batchId: string;
  /** `IMP-000007` — the batch's URL key (`importBatchPath`); immutable. */
  batchNumber: string;
  kind: ImportKind;
  status: ImportBatchStatus;
  uploadedAt: string;
  /** Null until Identity lands (T150) — every audited actor on this backend carries the same gap today. */
  uploadedBy: string | null;
  /** Identity's name for `uploadedBy`; null when unrecorded or erased. */
  uploadedByName?: string | null;
  additionCount: number;
  changeCount: number;
  errorCount: number;
  unchangedCount: number;
};

export async function listImportBatches(params: { kind?: ImportKind; page: number; pageSize?: number }) {
  const client = await catalog();
  const rawGet = client.GET as unknown as (
    url: "/api/catalog/imports",
    init: { params: { query: { kind?: ImportKind; page?: number; pageSize?: number } } },
  ) => Promise<{ data?: unknown; error?: unknown; response: Response }>;
  const call = rawGet("/api/catalog/imports", {
    params: { query: { kind: params.kind, page: params.page, pageSize: params.pageSize ?? 25 } },
  });
  return unwrap(call) as Promise<PagedResult<ImportBatchListItem>>;
}

export async function getImportBatchByNumber(batchNumber: string) {
  return unwrap(
    (await catalog()).GET("/api/catalog/imports/by-number/{batchNumber}", { params: { path: { batchNumber } } }),
  ) as Promise<ImportBatchListItem>;
}

/**
 * The batch a route's `:batchNumber` names. There is no by-id read, so a legacy id is looked up
 * among the newest batches and redirected to its number URL, query kept.
 * ponytail: an id older than the newest CATALOG_MAX_PAGE_SIZE batches resolves to null and the diff
 * still opens at its id URL (rows read by id; status unknown, so the API's own 409 guards a commit).
 */
export async function importBatchByKey(request: Request, key: string): Promise<ImportBatchListItem | null> {
  if (!isGuid(key)) return getImportBatchByNumber(key);
  const recent = await listImportBatches({ page: 1, pageSize: CATALOG_MAX_PAGE_SIZE });
  const batch = recent.items.find((row) => row.batchId.toLowerCase() === key.toLowerCase());
  if (!batch?.batchNumber) return batch ?? null; // no number yet (backend not upgraded): open in place
  throw redirectToCanonical(request, key, importBatchPath(batch.batchNumber));
}

// ---------------------------------------------------------------------------------------------
// Generated price list (001 US3, T065-T070) — `GET/POST/PUT` under `/tiers/{id}/prices`. Unlike
// `getTier`'s single-record read above, `ListTierPrices` is one of the routes GAP-4's fix actually
// reached: `PagedResultOfTierPriceRow` is a real, generated schema (`catalog.d.ts` confirms it),
// so the row shape below is this file's usual plain-local-type convention over a schema that
// happens to already be generated, not a stand-in for a missing one.
// ---------------------------------------------------------------------------------------------

export type PriceSource = "from_discount" | "manually_set";

export type TierPriceRow = {
  id: string;
  skuId: string;
  skuCode: string;
  productName: string;
  unitId: string;
  unitCode: string;
  rate: number;
  source: PriceSource;
  derivedFromBaseRate: number | null;
  isStale: boolean;
  staleReason: StaleReason;
  gstTreatment: "inclusive" | "exclusive";
};

export async function getTierPrices(
  tierId: string,
  params: { page: number; pageSize?: number; isStale?: boolean; skuId?: string; q?: string },
) {
  return unwrap(
    (await catalog()).GET("/api/catalog/tiers/{id}/prices", {
      params: {
        path: { id: tierId },
        query: {
          page: params.page,
          pageSize: params.pageSize ?? 50,
          isStale: params.isStale,
          skuId: params.skuId,
          // Server search over the whole tier: product name, SKU code or name, or unit code.
          // TODO(016 T080): remove cast after openapi regen (the snapshot predates `q`).
          ...({ q: params.q } as unknown as Record<string, never>),
        },
      },
    }),
  ) as unknown as Promise<PagedResult<TierPriceRow>>;
}

/** One SKU/unit combination `GenerateTierPricesHandler` declined to price — T068 lists these, never hides them. */
/** `skuCode`/`skuName`/`unitCode` are Catalog's labels beside the ids; null when unresolved. */
export type GenerationSkip = { skuId: string; unitId: string; discountPct: number; skuCode?: string | null; skuName?: string | null; unitCode?: string | null };

export type GenerateTierPricesResult = { rowsGenerated: number; elapsedMs: number; skipped: GenerationSkip[] };

/**
 * `POST /tiers/{id}/generate`'s generated 200 is `content?: never` — undeclared, the same GAP-4
 * tail as `createSku`/`setSkuUnits` above — but `TiersController.GenerateTierPrices` really
 * returns `Ok(new GenerateTierPricesResponse(RowsGenerated, ElapsedMs, Skipped))`. Narrow
 * runtime-parse cast, verified live against the running gateway 2026-09-10.
 */
export async function generateTierPrices(tierId: string) {
  const data = await unwrap((await catalog()).POST("/api/catalog/tiers/{id}/generate", { params: { path: { id: tierId } } }));
  return data as unknown as GenerateTierPricesResult;
}

/**
 * `PUT /tiers/{tierId}/prices/{priceId}` — `TiersController.EditPrice` binds only `priceId`
 * (`EditPrice(Guid priceId, EditTierPriceRequest request, ...)`), so the generated operation's
 * `path` type is `{priceId: string}` with no `tierId` at all, even though the URL template still
 * carries a `{tierId}` segment. openapi-fetch's path serializer substitutes every `{name}` token in
 * the URL by looking `params.path[name]` up **at runtime, by string, regardless of the declared
 * type** (`defaultPathSerializer`, `openapi-fetch/dist/index.cjs`) — so calling this with only
 * `priceId` sends the literal, unresolved `.../tiers/{tierId}/prices/...` segment and 404s.
 * `pathParams` is typed wider than the generated `path` type and passed in as a variable rather
 * than an object literal, so TypeScript's excess-property check (literals only) does not reject
 * the extra `tierId` key the URL actually needs. Verified live against the running gateway
 * 2026-09-10. The endpoint itself answers 204 No Content (`EditPrice` returns `NoContent()`), so
 * there is nothing to read back — the caller re-fetches the page it edited, same discipline as
 * `BasePrices.tsx`'s `setBasePrices` (FR-016: the row must come back `manually_set` from the
 * server, not be assumed so here).
 */
/** `gstTreatment` is re-typed only for the enum gap above `PriceRoundingMode`; the rest is generated. */
export type RevertToRuleResult = Omit<CatalogComponents["schemas"]["RevertToRuleResult"], "gstTreatment"> & { gstTreatment: GstTreatment };

/**
 * `POST /tiers/{tierId}/prices/{priceId}/revert-to-rule` (ASPL-10159, FR-045a) — the one way a
 * `manually_set` row goes back to `from_discount`. `dryRun` returns the rate without saving, so the
 * confirm step shows the API's figure, never one worked out here (constitution I).
 */
export async function revertTierPriceToRule(tierId: string, priceId: string, dryRun: boolean) {
  const data = await unwrap(
    (await catalog()).POST("/api/catalog/tiers/{tierId}/prices/{priceId}/revert-to-rule", {
      params: { path: { tierId, priceId }, query: { dryRun } },
    }),
  );
  return data as unknown as RevertToRuleResult;
}

export async function updateTierPrice(tierId: string, priceId: string, rate: number, gstTreatment?: GstTreatment) {
  const pathParams: { tierId: string; priceId: string } = { tierId, priceId };
  await unwrap(
    (await catalog()).PUT("/api/catalog/tiers/{tierId}/prices/{priceId}", {
      params: { path: pathParams },
      // D-04: omitted, the API keeps the row's current treatment.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- see the enum-gap comment above `PriceRoundingMode`
      body: { rate, gstTreatment } as any,
    }),
  );
}
